import { DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import { useCallback, useState } from 'react'

import { IconButton } from './IconButton'

interface CopyRequestButtonProps {
  /**
   * Label shown in the idle state, e.g. "Copy Tender Request"
   */
  label: string
  /**
   * Resolves the payload to serialize and copy to the clipboard
   */
  getContent: () => Promise<unknown> | unknown
}

/**
 * Copies a JSON payload to the clipboard, resolving it lazily so callers can
 * fetch a freshly mapped service request on click.
 */
export const CopyRequestButton = ({
  label,
  getContent,
}: CopyRequestButtonProps) => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'copied' | 'error'>(
    'idle',
  )

  const handleCopy = useCallback(async () => {
    setStatus('loading')
    try {
      const content = await getContent()

      await navigator.clipboard.writeText(JSON.stringify(content, null, 2))
      setStatus('copied')
      setTimeout(() => setStatus('idle'), 2000)
    } catch (error) {
      console.error(error)
      setStatus('error')
      setTimeout(() => setStatus('idle'), 3000)
    }
  }, [getContent])

  const buttonLabel =
    status === 'loading'
      ? 'Loading...'
      : status === 'copied'
        ? 'Copied!'
        : status === 'error'
          ? 'Failed'
          : label

  return (
    <IconButton
      className="btn-link text-info"
      icon={DocumentDuplicateIcon}
      onClick={handleCopy}
      title={status === 'copied' ? 'Copied!' : `${label} to clipboard`}
    >
      {buttonLabel}
    </IconButton>
  )
}
